"use client";

/**
 * Client side of /[locale]/trends - the places list.
 *
 * Every place the weekly trends cover (rayons, metro stations, settlements)
 * with its sale AZN/m², gap to the Bakı benchmark, a 12-week sparkline, the
 * change over a chosen period (1 / 4 / 12 weeks), the typical monthly rent,
 * gross yield and the active listing count. Each row links to its
 * /trends/[slug] page.
 *
 * Filters: a type pill (rayon / metro / settlement), a change-period pill and
 * a name search; a sort pill on phones, sortable column headers from md: up.
 * Everything is in memory (page.tsx passes the sale and rent snapshots), so
 * filtering and sorting are plain array ops.
 *
 * Phones get a two-line row (name + type, listings, vs Bakı under it; a
 * sparkline, AZN/m² and the change on the right), so nothing scrolls
 * sideways at 390 px.
 *
 * When `snapshot` is null (endpoint 404 / timeout) the list degrades to a
 * plain name list so the hub stays crawlable and unbroken across deploys.
 */
import { useCallback, useMemo, useState } from "react";
import Link from "@/lib/nav-link";
import type { Locale } from "@/lib/i18n-config";
import { t as translate, type TranslationKey } from "@/lib/i18n";
import { DISTRICTS, type District } from "@/lib/districts";
import { formatPrice } from "@/lib/api";
import { changeSign, roundChange, signedPct } from "@/lib/format/change";
import { ChevronRight, Search, X } from "lucide-react";
import WoWBadge from "@/components/trends/WoWBadge";
import SortHead from "@/components/trends/SortHead";
import Spark from "@/components/trends/Spark";
import { FilterOption, FilterPill } from "@/components/FilterPill";

export type PlaceKind = "rayon" | "metro" | "qesebe";

export interface DistrictSnapshot {
  slug: string;
  name: string;
  median_ppm: number | null;
  listing_count: number;
  wow_pct: number | null;
  indexable: boolean;
  /** Added 2026-10: type from bina's taxonomy, latest median price and a
   *  13-week AZN/m² series (oldest first). Optional so an older backend
   *  still renders. */
  kind?: PlaceKind | null;
  median_price?: number | null;
  series?: (number | null)[];
}

export interface SnapshotPayload {
  mode: "sale" | "rent";
  week: string;
  series_weeks?: string[];
  baku: { median_ppm: number | null; listing_count: number; as_of?: string; definition?: string };
  districts: DistrictSnapshot[];
}

interface Props {
  locale: Locale;
  snapshot: SnapshotPayload | null;
  /** The rent snapshot, for the monthly rent and yield columns. */
  rent?: SnapshotPayload | null;
}

type SortCol = "name" | "ppm" | "vsbaku" | "change" | "rent" | "yield" | "listings";
type SortDir = "asc" | "desc";
type Period = 1 | 4 | 12;
type KindFilter = "all" | PlaceKind;

const KINDS: PlaceKind[] = ["rayon", "metro", "qesebe"];
const PERIODS: Period[] = [1, 4, 12];
const SORTS: SortCol[] = ["listings", "ppm", "change", "vsbaku", "rent", "yield", "name"];
// A change smaller than this (in % points) is noise for the period: gray.
const NOISE: Record<Period, number> = { 1: 0.2, 4: 0.5, 12: 1 };

interface Row extends District {
  kind: PlaceKind | null;
  ppm: number | null;
  listings: number;
  indexable: boolean;
  wow: number | null;
  series: (number | null)[];
  salePrice: number | null;
  rent: number | null;
}

function mergeRows(districts: District[], sale: SnapshotPayload | null, rent: SnapshotPayload | null | undefined): Row[] {
  const bySlug = new Map<string, DistrictSnapshot>();
  for (const d of sale?.districts ?? []) bySlug.set(d.slug, d);
  const rentBySlug = new Map<string, DistrictSnapshot>();
  for (const d of rent?.districts ?? []) rentBySlug.set(d.slug, d);
  return districts.map((d) => {
    const s = bySlug.get(d.slug);
    const r = rentBySlug.get(d.slug);
    return {
      ...d,
      kind: s?.kind ?? null,
      ppm: s?.median_ppm ?? null,
      listings: s?.listing_count ?? 0,
      indexable: s?.indexable ?? false,
      wow: s?.wow_pct ?? null,
      series: s?.series ?? [],
      salePrice: s?.median_price ?? null,
      rent: r?.median_price ?? null,
    };
  });
}

// % a place sits above / below the Bakı benchmark AZN/m².
function vsBaku(r: Row, bakuPpm: number | null): number | null {
  if (r.ppm == null || !bakuPpm) return null;
  return ((r.ppm - bakuPpm) / bakuPpm) * 100;
}

// Change over `period` weeks from the series; the 1-week figure falls back
// to the backend's wow_pct when the series is missing (older backend).
function change(r: Row, period: Period): number | null {
  const s = r.series;
  const last = s.length - 1;
  const cur = last >= 0 ? s[last] : null;
  const prev = last - period >= 0 ? s[last - period] : null;
  if (cur != null && prev != null && prev > 0) return ((cur - prev) / prev) * 100;
  return period === 1 ? r.wow : null;
}

// Gross yield: a year of the typical rent over the typical sale price.
function grossYield(r: Row): number | null {
  if (r.rent == null || r.salePrice == null || r.salePrice <= 0) return null;
  return ((r.rent * 12) / r.salePrice) * 100;
}

function metric(r: Row, col: SortCol, period: Period, bakuPpm: number | null): number | null {
  switch (col) {
    case "ppm": return r.ppm;
    case "vsbaku": return vsBaku(r, bakuPpm);
    case "change": return change(r, period);
    case "rent": return r.rent;
    case "yield": return grossYield(r);
    case "listings": return r.listings;
    default: return null;
  }
}

function sortRows(rows: Row[], col: SortCol, dir: SortDir, period: Period, bakuPpm: number | null, locale: Locale): Row[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    if (col === "name") {
      const c = a.name.localeCompare(b.name, locale);
      return dir === "asc" ? c : -c;
    }
    const av = metric(a, col, period, bakuPpm);
    const bv = metric(b, col, period, bakuPpm);
    // Rows without the sorted metric always sink to the bottom.
    if (av == null && bv == null) return a.name.localeCompare(b.name, locale);
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av !== bv) return dir === "asc" ? av - bv : bv - av;
    return a.name.localeCompare(b.name, locale);
  });
  return copy;
}

const fold = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/ə/g, "e").replace(/ı/g, "i");

function numLocale(locale: Locale): string {
  return locale === "en" ? "en-US" : "ru-RU";
}

export default function TrendsHubClient({ locale, snapshot, rent }: Props) {
  const t = useCallback((k: TranslationKey) => translate(k, locale), [locale]);

  const [col, setCol] = useState<SortCol>("listings");
  const [dir, setDir] = useState<SortDir>("desc");
  const [period, setPeriod] = useState<Period>(1);
  const [kind, setKind] = useState<KindFilter>("all");
  const [query, setQuery] = useState("");
  const [pill, setPill] = useState<null | "kind" | "period" | "sort">(null);

  const bakuPpm = snapshot?.baku.median_ppm ?? null;
  const hasKinds = !!snapshot?.districts.some((d) => d.kind);
  const hasRent = !!rent?.districts.some((d) => d.median_price != null);

  const rows = useMemo(() => mergeRows(DISTRICTS, snapshot, rent), [snapshot, rent]);
  // Offer a period only when the series reaches back that far (1 week always,
  // it falls back to wow_pct).
  const periods = useMemo(() => {
    const longest = Math.max(0, ...rows.map((r) => r.series.length));
    return PERIODS.filter((p) => p === 1 || longest > p);
  }, [rows]);
  const kindCounts = useMemo(() => {
    const c: Record<PlaceKind, number> = { rayon: 0, metro: 0, qesebe: 0 };
    for (const r of rows) if (r.kind) c[r.kind] += 1;
    return c;
  }, [rows]);
  const filtered = useMemo(() => {
    const q = fold(query.trim());
    return rows.filter((r) =>
      (kind === "all" || r.kind === kind) && (!q || fold(r.name).includes(q)));
  }, [rows, query, kind]);
  const sorted = useMemo(
    () => sortRows(filtered, col, dir, period, bakuPpm, locale),
    [filtered, col, dir, period, bakuPpm, locale],
  );

  // Click a header: flip direction if it's the active column, else switch to
  // it (names default A->Z, numbers default high->low - the useful first view).
  function onSort(next: SortCol) {
    if (next === col) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setCol(next);
      setDir(next === "name" ? "asc" : "desc");
    }
  }

  const periodLabel = t(`trends.list.period.${period}` as TranslationKey);
  const kindValue = kind === "all" ? null : t(`trends.list.kind.${kind}` as TranslationKey);
  const filtersSet = kind !== "all" || query.trim() !== "";

  function reset() {
    setKind("all");
    setQuery("");
  }

  const sortLabel = (c: SortCol) =>
    c === "change" ? `${t("trends.list.sort.change")} (${periodLabel})` : t(`trends.list.sort.${c}` as TranslationKey);

  return (
    <section className="mt-8" aria-labelledby="trends-list-title">
      <h2 id="trends-list-title" className="sr-only">{t("trends.index.title")}</h2>

      {/* Filter bar: search, then pills (type, change period, phone sort). */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative block w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
          <input
            type="text"
            enterKeyHint="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("trends.list.search")}
            aria-label={t("trends.list.search")}
            className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label={t("trends.index.search_clear")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </label>

        {snapshot && hasKinds && (
          <FilterPill
            label={t("trends.list.kind")}
            value={kindValue}
            active={kind !== "all"}
            open={pill === "kind"}
            onOpenChange={(o) => setPill(o ? "kind" : null)}
          >
            <div className="space-y-0.5">
              <FilterOption on={kind === "all"} meta={rows.length} onClick={() => { setKind("all"); setPill(null); }}>
                {t("trends.list.kind.all")}
              </FilterOption>
              {KINDS.map((k) => (
                <FilterOption key={k} on={kind === k} meta={kindCounts[k]} onClick={() => { setKind(k); setPill(null); }}>
                  {t(`trends.list.kind.${k}` as TranslationKey)}
                </FilterOption>
              ))}
            </div>
          </FilterPill>
        )}

        {snapshot && (
          <FilterPill
            label={t("trends.list.period")}
            value={periodLabel}
            active={false}
            open={pill === "period"}
            onOpenChange={(o) => setPill(o ? "period" : null)}
          >
            <div className="space-y-0.5">
              {periods.map((p) => (
                <FilterOption key={p} on={period === p} onClick={() => { setPeriod(p); setPill(null); }}>
                  {t(`trends.list.period.${p}` as TranslationKey)}
                </FilterOption>
              ))}
            </div>
          </FilterPill>
        )}

        {filtersSet && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex h-9 items-center gap-1 rounded-lg px-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <X className="h-4 w-4" aria-hidden />
            {t("trends.list.reset")}
          </button>
        )}

        {snapshot && (
          <FilterPill
            className="md:hidden"
            align="right"
            label={t("trends.list.sort")}
            value={sortLabel(col)}
            active={false}
            open={pill === "sort"}
            onOpenChange={(o) => setPill(o ? "sort" : null)}
          >
            <div className="space-y-0.5">
              {SORTS.filter((c) => c !== "rent" && c !== "yield" ? true : hasRent).map((c) => (
                <FilterOption
                  key={c}
                  on={col === c}
                  onClick={() => {
                    if (c === col) setDir((d) => (d === "asc" ? "desc" : "asc"));
                    else { setCol(c); setDir(c === "name" ? "asc" : "desc"); }
                    setPill(null);
                  }}
                  meta={col === c ? (dir === "asc" ? "↑" : "↓") : undefined}
                >
                  {sortLabel(c)}
                </FilterOption>
              ))}
            </div>
          </FilterPill>
        )}
      </div>

      <p className="mt-3 text-sm tabular-nums text-gray-600" aria-live="polite">
        {t("trends.list.count").replace("{n}", String(sorted.length))}
      </p>

      <div className="mt-2 overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {snapshot && sorted.length > 0 && (
          <div className="hidden items-center gap-3 border-b border-gray-100 px-4 py-2.5 text-xs font-semibold text-gray-500 md:flex">
            <SortHead className="flex min-w-0 flex-1 justify-start" label={t("trends.index.col.area")} active={col === "name"} dir={dir} onClick={() => onSort("name")} />
            <SortHead className="flex w-20 justify-end" label="AZN/m²" active={col === "ppm"} dir={dir} onClick={() => onSort("ppm")} />
            <SortHead className="flex w-16 justify-end" label={t("trends.index.col.vsbaku")} active={col === "vsbaku"} dir={dir} onClick={() => onSort("vsbaku")} />
            <span className="hidden w-20 text-right lg:block">{t("trends.list.col.trend")}</span>
            <SortHead className="flex w-20 justify-end" label={periodLabel} active={col === "change"} dir={dir} onClick={() => onSort("change")} />
            {hasRent && (
              <>
                <SortHead className="hidden w-28 justify-end lg:flex" label={t("trends.list.col.rent")} active={col === "rent"} dir={dir} onClick={() => onSort("rent")} />
                <span className="hidden w-24 justify-end lg:flex" title={t("trends.list.yield_hint")}>
                  <SortHead className="flex justify-end" label={t("trends.list.col.yield")} active={col === "yield"} dir={dir} onClick={() => onSort("yield")} />
                </span>
              </>
            )}
            <SortHead className="flex w-24 justify-end" label={t("trends.index.col.listings")} active={col === "listings"} dir={dir} onClick={() => onSort("listings")} />
            <span className="w-4 shrink-0" />
          </div>
        )}

        {sorted.length === 0 ? (
          <div className="flex flex-col items-start gap-3 px-4 py-8 sm:px-6">
            <p className="text-sm text-gray-600">
              {query.trim()
                ? t("trends.list.empty").replace("{q}", query.trim())
                : t("trends.list.empty_kind")}
            </p>
            <button
              type="button"
              onClick={reset}
              className="inline-flex h-9 items-center rounded-lg bg-indigo-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              {t("trends.list.reset")}
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {sorted.map((r) => (
              <li key={r.slug}>
                {snapshot ? (
                  <PlaceRow locale={locale} row={r} period={period} bakuPpm={bakuPpm} hasRent={hasRent} sortCol={col} />
                ) : (
                  <Link
                    href={`/${locale}/trends/${r.slug}`}
                    className="group flex items-center gap-2 px-4 py-3 transition-colors hover:bg-gray-50"
                  >
                    <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-gray-900">{r.name}</span>
                    <span className="text-xs font-medium text-gray-700 underline-offset-2 group-hover:underline">{t("trends.index.card.cta")}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" aria-hidden />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {snapshot && (
        <p className="mt-3 text-xs leading-relaxed text-gray-500">{t("trends.list.caption")}</p>
      )}
    </section>
  );
}

// -- Row ----------------------------------------------------------------------

function PlaceRow({
  locale,
  row,
  period,
  bakuPpm,
  hasRent,
  sortCol,
}: {
  locale: Locale;
  row: Row;
  period: Period;
  bakuPpm: number | null;
  hasRent: boolean;
  sortCol: SortCol;
}) {
  const t = (k: TranslationKey) => translate(k, locale);
  const na = t("common.na");
  const dim = !row.indexable;
  const vb = vsBaku(row, bakuPpm);
  const ch = change(row, period);
  const yld = grossYield(row);
  const nl = numLocale(locale);
  const yieldText = yld == null ? na : `${yld.toLocaleString(nl, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  const vbText = vb == null ? na : signedPct(vb, 0);
  const kindTag = row.kind ? t(`trends.list.tag.${row.kind}` as TranslationKey) : null;

  // Sparkline: the last 13 weeks, coloured by the chosen period's change
  // (rose up, emerald down, gray flat) through currentColor so both themes work.
  // Same rule as the WoWBadge next to it: rounded to one decimal first.
  const chSign = ch == null || Math.abs(roundChange(ch, 1)) < NOISE[period] ? 0 : changeSign(ch, 1);
  const sparkTone = chSign > 0 ? "text-rose-500" : chSign < 0 ? "text-emerald-600" : "text-gray-400";
  const series = row.series.map((v) => ({ median_ppm: v }));

  // Phones: the sorted metric joins the subline when it has no cell there.
  const extra =
    sortCol === "rent" ? (row.rent == null ? null : `${t("trends.list.col.rent")} ${formatPrice(row.rent)}`)
      : sortCol === "yield" ? `${t("trends.list.col.yield")} ${yieldText}`
        : null;
  const sub = [
    extra,
    kindTag,
    vb == null ? null : t("trends.list.vsbaku_inline").replace("{pct}", vbText),
    t("trends.list.listings_n").replace("{n}", formatPrice(row.listings)),
  ].filter(Boolean).join(" · ");

  return (
    <Link
      href={`/${locale}/trends/${row.slug}`}
      title={dim ? t("trends.list.thin") : undefined}
      className={`group flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none ${dim ? "opacity-60" : ""}`}
    >
      {/* Name (+ the phone subline) */}
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="min-w-0 max-w-full shrink-0 truncate text-[15px] font-semibold text-gray-900 underline-offset-2 group-hover:underline">
            {row.name}
          </span>
          {kindTag && <span className="hidden min-w-0 truncate text-xs text-gray-400 md:inline">{kindTag}</span>}
        </span>
        <span className="mt-0.5 block truncate text-xs tabular-nums text-gray-500 md:hidden">{sub}</span>
      </span>

      {/* Phone: sparkline + price over change */}
      <span className={`shrink-0 md:hidden ${sparkTone}`}>
        <Spark series={series} color="currentColor" width={44} height={22} />
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1 md:hidden">
        <span className="text-sm font-bold tabular-nums text-gray-900">
          {row.ppm == null ? na : formatPrice(row.ppm)}
          {row.ppm != null && <span className="ml-0.5 text-[10px] font-medium text-gray-400">AZN/m²</span>}
        </span>
        <WoWBadge wow={ch} size="sm" threshold={NOISE[period]} />
      </span>

      {/* md+: one cell per column */}
      <span className="hidden w-20 text-right text-sm font-bold tabular-nums text-gray-900 md:block">
        {row.ppm == null ? na : formatPrice(row.ppm)}
      </span>
      <span className={`hidden w-16 text-right text-sm tabular-nums md:block ${vb == null ? "text-gray-400" : "text-gray-600"}`}>
        {vbText}
      </span>
      <span className={`hidden w-20 justify-end lg:flex ${sparkTone}`}>
        <Spark series={series} color="currentColor" width={80} height={24} />
      </span>
      <span className="hidden w-20 justify-end md:flex">
        <WoWBadge wow={ch} size="sm" threshold={NOISE[period]} />
      </span>
      {hasRent && (
        <>
          <span className={`hidden w-28 text-right text-sm tabular-nums lg:block ${row.rent == null ? "text-gray-400" : "text-gray-700"}`}>
            {row.rent == null ? na : formatPrice(row.rent)}
          </span>
          <span className={`hidden w-24 text-right text-sm tabular-nums lg:block ${yld == null ? "text-gray-400" : "text-gray-700"}`}>
            {yieldText}
          </span>
        </>
      )}
      <span className="hidden w-24 text-right text-sm tabular-nums text-gray-500 md:block">
        {formatPrice(row.listings)}
      </span>

      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-500" aria-hidden />
    </Link>
  );
}
